import { useState } from "react";
import { Col, Row } from "reactstrap";
import {
  StatusChip,
  Text,
  Icons,
  Card,
  BadgeColor,
} from "@pushpress/shared-components";
import styles from "./OnboardingTaskListCard.module.scss";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import { updateTask, Task, TaskList } from "../../../../apis/taskApi";
import { useMutation } from "@tanstack/react-query";
import TaskListItem from "./TaskListItem";
import { GetStartedEvent } from "../../events";

type Props = {
  taskList: TaskList;
};

export enum TaskListStatus {
  NOT_STARTED = "NOT STARTED",
  IN_PROGRESS = "IN PROGRESS",
  COMPLETED = "COMPLETED",
}

export const TaskStatusColors: { [key in TaskListStatus]: BadgeColor } = {
  [TaskListStatus.NOT_STARTED]: BadgeColor.Gray,
  [TaskListStatus.IN_PROGRESS]: BadgeColor.Warning,
  [TaskListStatus.COMPLETED]: BadgeColor.Success,
};

const getNewStatus = (newTaskList: TaskList) => {
  const completedTasks = newTaskList.tasks.filter((t) => t.isComplete).length;
  if (completedTasks === 0) {
    return TaskListStatus.NOT_STARTED;
  } else if (completedTasks < newTaskList.tasks.length) {
    return TaskListStatus.IN_PROGRESS;
  }
  return TaskListStatus.COMPLETED;
};

function OnboardingTaskListCard({ taskList }: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const { trackEvent } = useAnalytics();
  const updateTaskMutation = useMutation(updateTask);
  const [data, setData] = useState(taskList);
  const { title, tasks, taskListId } = data;
  const tasksCompleted = tasks.filter((task) => task.isComplete === 1).length;
  const status = getNewStatus(data);

  const handleUpdateTask = (task: Task, markAsComplete: boolean) => {
    const newData = { ...data };
    const taskFromlist = newData.tasks.find((t) => t.id === task.id);
    if (!taskFromlist) return;
    taskFromlist.isComplete = markAsComplete ? 1 : 0;
    setData({ ...newData });
    updateTaskMutation.mutate({ taskUuid: task.uuid, markAsComplete });
  };

  return (
    <>
      <Card className="p-4">
        <Row
          onClick={() => {
            trackEvent(GetStartedEvent.TaskListToggled, {
              product: "core",
              task_list_name: title,
              task_list_id: taskListId,
              state: isOpen ? "closed" : "opened",
              task_count: data.tasks.length,
              completed_task_count: tasksCompleted,
            });
            setIsOpen(!isOpen);
          }}
          className={styles.cardHeader}
        >
          <span className={styles.iconSpan}>
            <div
              className={`${styles.icon} ${
                isOpen ? styles.hidden : styles.visible
              }`}
            >
              <Icons.CaretRight />
            </div>
            <div
              className={`${styles.icon} ${
                isOpen ? styles.visible : styles.hidden
              }`}
            >
              <Icons.CaretDown />
            </div>
          </span>

          <Col>
            <div className={styles.headerContent}>
              <div>
                <Text variant="heading-4">{title}</Text>
                <Text
                  color="wolf-600"
                  variant="body-md"
                  style={{ marginTop: ".2rem" }}
                >{`${tasksCompleted} of ${tasks.length} completed`}</Text>
              </div>

              <div>
                <StatusChip label={status} color={TaskStatusColors[status]} />
              </div>
            </div>
          </Col>
        </Row>

        {isOpen && (
          <Col xs="12" className={styles.cardContent}>
            <div className={styles.divider} />

            {data.tasks.map((task: Task) => (
              <TaskListItem
                key={task.id}
                taskList={taskList}
                task={task}
                handleUpdateTask={() =>
                  handleUpdateTask(task, !task.isComplete)
                }
              />
            ))}
          </Col>
        )}
      </Card>
    </>
  );
}

export default OnboardingTaskListCard;
